import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { UnterschriftWeg } from '../../../services/sifa/types';
import { legeSignaturAnfrageAn, frageSignaturAb } from '../../../services/sifa/sifaPublicService';
import { describeError } from '../../../services/errorMessage';
import { SignaturePad } from '../../common/SignaturePad';
import { QR_ABLAUF_MS, QR_POLL_MS, UNTERSCHRIFT_MAX_BREITE, signUrl } from './teilnahmeSeiteLogic';
import { DATENSCHUTZ_HAEKCHEN } from './abschlussLogic';
import { FEHLERKASTEN, HINWEISKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER, TEXTLINK } from './teilnahmeSeiteStil';

type QrStatus = 'keiner' | 'erzeuge' | 'wartet' | 'abgelaufen' | 'fehler';

/** Ein Pflicht-Haekchen mit grosser Tippflaeche (wie „verstanden" im Formular). */
const PflichtHaekchen: React.FC<{
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (wert: boolean) => void;
  children: React.ReactNode;
}> = ({ id, checked, disabled, onChange, children }) => (
  <label
    htmlFor={id}
    className={`flex items-start gap-3 p-4 min-h-[56px] rounded-xl border-2 cursor-pointer transition-all ${
      checked ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-6 h-6 shrink-0 accent-blue-600"
    />
    <span className="text-base text-slate-800 leading-snug">{children}</span>
  </label>
);

/**
 * Abschluss: zwei Pflicht-Haekchen, dann die Unterschrift
 * (docs/sifa-ausbau2-plan.md §0.3, §3.3).
 *
 * 1. Bestaetigung — der Satz kommt fertig herein (`bestaetigungText`, aus
 *    baueBestaetigung) und geht genau so an die Datenbank.
 * 2. Kenntnisnahme der Datenschutzhinweise; der Link oeffnet sie in einem
 *    neuen Tab, damit der Ablauf hier stehen bleibt.
 *
 * Erst mit beiden Haekchen gibt es einen Weg zur Unterschrift:
 * - PC (Echtbetrieb): QR-Code aufs Handy (Muster QuoteEditor):
 *   sifa_signatur_anfrage_anlegen liefert einen Einmal-Token, das Handy
 *   oeffnet #/sign/<token>?u=1 und schreibt ueber sifa_submit_signature; hier
 *   wird alle 2,5 s abgefragt, nach 10 min laeuft die Anfrage ab. Ausweg:
 *   direkt mit der Maus (SignaturePad als Overlay).
 * - PC im Testlauf: nur direkt (kein QR — es gibt keine Sitzung).
 * - Handy/Tablet (`touch`): ein Knopf oeffnet die Vollbild-Unterschrift der
 *   Seite (UnterweisungTeilnahmePage).
 */
export const UnterschriftAnsicht: React.FC<{
  teilnahmeToken: string | null;
  busy: boolean;
  fehler: string | null;
  /** Der Bestaetigungssatz, woertlich wie gespeichert. */
  bestaetigungText: string;
  /** Link zu den Datenschutzhinweisen (#/datenschutz/unterweisung?l=… bzw. ?t=…). */
  datenschutzHref: string;
  bestaetigt: boolean;
  datenschutzGelesen: boolean;
  onBestaetigt: (wert: boolean) => void;
  onDatenschutzGelesen: (wert: boolean) => void;
  /** QR-Bruecke anbieten — nur im Echtbetrieb am PC. */
  mitQr: boolean;
  /** Handy/Tablet: die Seite zeigt die Vollbild-Unterschrift statt des Overlays. */
  touch: boolean;
  /**
   * Das Unterschriftsfeld ist offen (Overlay am PC, Vollbild am Handy). Der
   * Zustand lebt im Hook: der schliesst es bei jedem weggenommenen Haekchen,
   * auch wenn eine neue Datenschutz-Fassung das Haekchen zuruecksetzt — sonst
   * spraenge das Feld beim erneuten Anhaken von selbst wieder auf.
   */
  unterschriftOffen: boolean;
  onUnterschriftOeffnen: () => void;
  onUnterschriftSchliessen: () => void;
  /** Testlauf: Hinweis, dass die Unterschrift nicht gespeichert wird. */
  test?: boolean;
  onUnterschrift: (dataUrl: string, weg: UnterschriftWeg) => void;
  onZurueck: () => void;
}> = ({
  teilnahmeToken,
  busy,
  fehler,
  bestaetigungText,
  datenschutzHref,
  bestaetigt,
  datenschutzGelesen,
  onBestaetigt,
  onDatenschutzGelesen,
  mitQr,
  touch,
  unterschriftOffen,
  onUnterschriftOeffnen,
  onUnterschriftSchliessen,
  test = false,
  onUnterschrift,
  onZurueck,
}) => {
  const [qr, setQr] = useState<{ token: string; bild: string; url: string; erzeugt: number } | null>(null);
  const [qrStatus, setQrStatus] = useState<QrStatus>('keiner');
  const [qrFehler, setQrFehler] = useState('');
  const frei = bestaetigt && datenschutzGelesen;
  const qrMoeglich = mitQr && !touch;
  // Der Handler wechselt bei jedem Render — das Polling soll deshalb nicht neu anlaufen.
  const onUnterschriftRef = useRef(onUnterschrift);
  useEffect(() => {
    onUnterschriftRef.current = onUnterschrift;
  }, [onUnterschrift]);

  const qrErzeugen = async () => {
    if (!teilnahmeToken || !frei) return;
    setQrStatus('erzeuge');
    setQrFehler('');
    try {
      const token = await legeSignaturAnfrageAn(teilnahmeToken);
      const url = signUrl(window.location.origin, window.location.pathname, token);
      const bild = await QRCode.toDataURL(url, { width: 240, margin: 1 });
      setQr({ token, bild, url, erzeugt: Date.now() });
      setQrStatus('wartet');
    } catch (e) {
      setQrFehler(describeError(e));
      setQrStatus('fehler');
    }
  };

  // Abgefragt wird nur, solange beide Haekchen stehen: nimmt jemand eines weg,
  // ruht die Bruecke; die Unterschrift vom Handy wird erst danach uebernommen.
  // Der Ablauf zaehlt ab dem Erzeugen weiter (Server: 10 min), nicht ab dem Haekchen.
  useEffect(() => {
    if (!qr || !teilnahmeToken || !frei) return;
    let beendet = false;
    const takt = setInterval(async () => {
      try {
        const signatur = await frageSignaturAb(teilnahmeToken, qr.token);
        if (signatur && !beendet) {
          beendet = true;
          clearInterval(takt);
          clearTimeout(ablauf);
          setQr(null);
          setQrStatus('keiner');
          onUnterschriftRef.current(signatur, 'qr');
        }
      } catch {
        // Naechster Takt versucht es erneut; ein Netzwerkruckler bricht nichts ab.
      }
    }, QR_POLL_MS);
    const ablauf = setTimeout(() => {
      beendet = true;
      clearInterval(takt);
      setQr(null);
      setQrStatus('abgelaufen');
    }, Math.max(0, QR_ABLAUF_MS - (Date.now() - qr.erzeugt)));
    return () => {
      beendet = true;
      clearInterval(takt);
      clearTimeout(ablauf);
    };
  }, [qr, teilnahmeToken, frei]);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">Bestätigung und Unterschrift</h2>
        <p className="text-slate-600">
          {test
            ? 'Im Testlauf läuft alles wie bei einer echten Teilnahme — gespeichert wird die Unterschrift aber nicht.'
            : 'Bitte bestätige beide Punkte. Danach unterschreibst du; die Unterschrift wird auf deinem Nachweis abgedruckt.'}
        </p>
      </div>

      {fehler && (
        <div className={FEHLERKASTEN} role="alert">
          {fehler}
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="sr-only">Pflichtbestätigungen vor der Unterschrift</legend>
        <PflichtHaekchen id="abschluss-bestaetigung" checked={bestaetigt} disabled={busy} onChange={onBestaetigt}>
          {bestaetigungText}
        </PflichtHaekchen>
        <PflichtHaekchen id="abschluss-datenschutz" checked={datenschutzGelesen} disabled={busy} onChange={onDatenschutzGelesen}>
          {DATENSCHUTZ_HAEKCHEN.vor}
          <a href={datenschutzHref} target="_blank" rel="noopener" className={TEXTLINK}>
            {DATENSCHUTZ_HAEKCHEN.link}
          </a>
          {DATENSCHUTZ_HAEKCHEN.nach}
        </PflichtHaekchen>
      </fieldset>

      {!frei && (
        <div className={HINWEISKASTEN} aria-live="polite">
          Bitte setze beide Häkchen — danach kannst du unterschreiben.
        </div>
      )}

      {touch ? (
        <button type="button" onClick={onUnterschriftOeffnen} disabled={busy || !frei} className={KNOPF_PRIMAER}>
          Jetzt unterschreiben
        </button>
      ) : (
        <>
          {qrMoeglich && (
            <section className="p-4 border border-slate-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-slate-800">Mit dem Smartphone unterschreiben</h3>
              <p className="text-slate-600">
                Scanne den QR-Code mit deinem Handy, unterschreibe dort mit dem Finger — die Unterschrift erscheint
                hier automatisch.
              </p>

              {qrStatus === 'wartet' && qr && frei && (
                <div className="flex flex-col items-center gap-2">
                  <img src={qr.bild} alt="QR-Code zum Unterschreiben auf dem Smartphone" className="w-60 h-60" />
                  <p className="text-sm text-slate-500" aria-live="polite">
                    Warte auf die Unterschrift vom Handy… (der Code gilt 10 Minuten)
                  </p>
                </div>
              )}
              {qrStatus === 'wartet' && qr && !frei && (
                <div className={HINWEISKASTEN}>Der QR-Code erscheint wieder, sobald beide Häkchen gesetzt sind.</div>
              )}
              {qrStatus === 'abgelaufen' && <div className={HINWEISKASTEN}>Der QR-Code ist abgelaufen.</div>}
              {qrStatus === 'fehler' && <div className={FEHLERKASTEN}>{qrFehler}</div>}

              {qrStatus !== 'wartet' && (
                <button
                  type="button"
                  onClick={qrErzeugen}
                  disabled={busy || !frei || qrStatus === 'erzeuge' || !teilnahmeToken}
                  className={KNOPF_PRIMAER}
                >
                  {qrStatus === 'erzeuge' ? 'Erzeuge QR-Code…' : qrStatus === 'keiner' ? 'QR-Code anzeigen' : 'Neuen QR-Code erzeugen'}
                </button>
              )}
            </section>
          )}

          {qrMoeglich ? (
            <section className="p-4 border border-slate-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-slate-800">Kein Smartphone zur Hand?</h3>
              <button type="button" onClick={onUnterschriftOeffnen} disabled={busy || !frei} className={KNOPF_SEKUNDAER}>
                Stattdessen hier mit der Maus unterschreiben
              </button>
            </section>
          ) : (
            <button type="button" onClick={onUnterschriftOeffnen} disabled={busy || !frei} className={KNOPF_PRIMAER}>
              Hier unterschreiben
            </button>
          )}
        </>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} disabled={busy} className={KNOPF_SEKUNDAER}>
          Zurück
        </button>
      </div>

      {unterschriftOffen && frei && !touch && (
        <SignaturePad
          alsOverlay
          titel={test ? 'Unterschrift — Testlauf' : 'Unterschrift — Unterweisung'}
          hinweis={
            test
              ? 'Testlauf: die Unterschrift wird nicht gespeichert.'
              : 'Bitte im Feld unterschreiben. Die Unterschrift wird auf deinem Nachweis abgedruckt.'
          }
          maxBreite={UNTERSCHRIFT_MAX_BREITE}
          uebernehmenLabel="Unterschrift übernehmen"
          busy={busy}
          fehler={fehler}
          onUebernehmen={(dataUrl) => onUnterschrift(dataUrl, 'direkt')}
          onAbbrechen={onUnterschriftSchliessen}
        />
      )}
    </div>
  );
};
